/**
 * Renderização de Markdown com realce de sintaxe, botão de cópia
 * e sanitização (DOMPurify) — sem inserir HTML não confiável.
 */
import { useEffect, useMemo, useRef } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import hljs from "highlight.js/lib/common";

marked.setOptions({ gfm: true, breaks: true });

function enhanceCodeBlocks(root: HTMLDivElement | null): void {
  if (!root) return;
  for (const pre of Array.from(root.querySelectorAll("pre"))) {
    if (pre.dataset.enhanced === "1") continue;
    pre.dataset.enhanced = "1";
    const code = pre.querySelector("code");
    if (!code) continue;

    const langClass = Array.from(code.classList).find((c) => c.startsWith("language-"));
    const lang = langClass ? langClass.replace("language-", "") : "";

    try {
      if (lang && hljs.getLanguage(lang)) {
        hljs.highlightElement(code);
      } else if (!lang && code.textContent && code.textContent.length < 4000) {
        hljs.highlightElement(code);
      }
    } catch {
      /* linguagem desconhecida — segue sem realce */
    }

    const head = document.createElement("div");
    head.className = "code-head";
    const label = document.createElement("span");
    label.textContent = lang || "código";
    const copy = document.createElement("button");
    copy.type = "button";
    copy.className = "btn ghost sm";
    copy.textContent = "Copiar";
    copy.addEventListener("click", () => {
      void navigator.clipboard.writeText(code.textContent ?? "").then(() => {
        copy.textContent = "Copiado!";
        setTimeout(() => {
          copy.textContent = "Copiar";
        }, 1600);
      });
    });
    head.append(label, copy);
    pre.insertBefore(head, code);
  }
}

export function Markdown({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const html = useMemo(
    () => DOMPurify.sanitize(marked.parse(text ?? "", { async: false }) as string),
    [text],
  );

  useEffect(() => {
    enhanceCodeBlocks(ref.current);
  }, [html]);

  return <div className="markdown" ref={ref} dangerouslySetInnerHTML={{ __html: html }} />;
}
